// Local design-preview client. Used ONLY when VITE_PREVIEW=1 so the dashboard
// can render fully-populated pages without Clerk auth or a live control plane
// (production Clerk keys refuse to run on localhost). Never imported in prod
// builds unless the flag is set. See useApi() in api.ts.

import type {
  ApiClient,
  Bundle,
  SkillKey,
  SkillSummary,
  CheckoutResponse,
  CreatedToken,
  Session,
  Token,
  Usage,
} from "./api.ts";

const ago = (ms: number) => new Date(Date.now() - ms).toISOString();
const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const delay = <T>(value: T, ms = 450): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

const SESSIONS: Session[] = [
  {
    id: "1920b89e3f",
    agent: "claude",
    project: "stift-cloud",
    host: "macbook-pro",
    title: "Reskin the dashboard to match the docs site",
    size: 482_113,
    updated_at: ago(8 * MIN),
  },
  {
    id: "7c41aa02de",
    agent: "codex",
    project: "stift-cloud",
    host: "macbook-pro",
    title: "Wire up Stripe per-seat billing",
    size: 1_204_558,
    updated_at: ago(3 * HOUR),
  },
  {
    id: "a0b1c2d3e4",
    agent: "gemini",
    project: "engine",
    host: "dev-box-lxc",
    title: "Quota enforcement in r2store Put",
    size: 96_842,
    updated_at: ago(26 * HOUR),
  },
  {
    id: "f5e6d7c8b9",
    agent: "cursor",
    project: "ally-website",
    host: "workstation",
    title: "",
    size: 2_998_311,
    updated_at: ago(3 * DAY),
  },
  {
    id: "9a8b7c6d5e",
    agent: "opencode",
    project: "visualhub.ai",
    host: "thinkpad-x1",
    title: "Refactor the upload pipeline and add retries on transient R2 errors",
    size: 651_207,
    updated_at: ago(11 * DAY),
  },
];

const TOKENS: Token[] = [
  {
    id: "tok_laptop",
    name: "macbook-pro",
    admin: true,
    created_at: ago(40 * DAY),
    last_used: ago(8 * MIN),
  },
  {
    id: "tok_ci",
    name: "ci-runner",
    admin: false,
    created_at: ago(12 * DAY),
    last_used: ago(2 * DAY),
  },
  {
    id: "tok_old",
    name: "old-desktop",
    admin: false,
    created_at: ago(90 * DAY),
    last_used: null,
  },
];

const USAGE: Usage = {
  plan: "free",
  bytes: 5_433_031,
  sessions: 5,
  max_bytes: null,
  max_sessions: 50,
};

// Skills: mirrors the design/skills mock rows.
const MD = `---
name: deploy-checklist
description: Pre-deploy verification steps for the engine container
---

# Deploy checklist

Run before every wrangler deploy of the engine container.

1. go test ./... in stift-cloud/engine with -mod=vendor
2. Confirm vendor/ is in sync: go mod vendor && git diff --exit-code vendor
3. Apply pending D1 migrations to production first
4. Check INTERNAL_SECRET matches between engine and control-plane
5. Smoke test: stift push --latest from a clean machine

Never deploy on a Friday after 16:00.
`;

const skill = (
  name: string,
  description: string,
  scope: string,
  agent: string,
  project: string,
  version: number,
  author: string,
  agoMs: number,
  files = 3,
  size = 10_956,
): SkillSummary => ({
  scope,
  agent,
  project,
  name,
  version,
  files,
  size,
  skills: name.startsWith("skills/")
    ? [{ path: "SKILL.md", name: name.slice(7), description }]
    : [],
  author,
  host: author === "you" ? "macbook-pro" : "workstation",
  updated_at: ago(agoMs),
});

const SKILLS: SkillSummary[] = [
  skill("skills/deploy-checklist", "Pre-deploy verification steps for the engine container", "org", "claude", "", 6, "moe", 2 * HOUR),
  skill("skills/pr-review", "House code-review conventions and severity rubric", "org", "claude", "", 3, "moe", 5 * DAY),
  skill("skills/cloudflare-runbook", "Wrangler, D1 migrations and R2 gotchas", "org", "claude", "", 2, "sara", 12 * DAY),
  skill("skills/dataviz", "Chart method, palette validator and mark specs", "user", "claude", "", 14, "you", 8 * MIN, 9, 48_120),
  skill("skills/go-testing", "Table-driven tests, httptest patterns, race detector", "user", "codex", "", 4, "you", 1 * DAY),
  skill("CLAUDE.md", "", "user", "claude", "", 9, "you", 3 * DAY, 1, 2_410),
  skill("skills/release-vendoring", "Re-vendor the engine module and cut a release", "project", "claude", "/root/Projects/stift-cloud", 21, "you", 40 * MIN),
];

const HISTORY_WHO = [
  ["moe", "macbook-pro", 2 * HOUR],
  ["moe", "dashboard", 3 * DAY],
  ["sara", "workstation", 9 * DAY],
  ["sara", "workstation", 12 * DAY],
  ["moe", "macbook-pro", 20 * DAY],
  ["moe", "macbook-pro", 31 * DAY],
] as const;

function previewBundle(key: SkillKey, version: number, head: number): Bundle {
  const row = SKILLS.find((s) => s.name === key.name && s.scope === key.scope);
  const idx = Math.min(HISTORY_WHO.length - 1, Math.max(0, head - version));
  const [author, host, when] = HISTORY_WHO[idx];
  return {
    scope: key.scope,
    agent: key.agent,
    project: key.project,
    name: key.name,
    version,
    parent: version - 1,
    host,
    author,
    created: ago(when),
    files: [
      { path: "SKILL.md", sha256: "a".repeat(64), size: 1_228, mode: 0o644 },
      { path: "scripts/smoke.sh", sha256: "b".repeat(64), size: 3_481, mode: 0o755 },
      { path: "reference/d1-migrations.md", sha256: "c".repeat(64), size: 6_247, mode: 0o644 },
    ],
    skills: row?.skills ?? [],
    head,
  };
}

function previewHead(key: SkillKey): number {
  return SKILLS.find((s) => s.name === key.name && s.scope === key.scope)?.version ?? 1;
}

// A structurally-typed stand-in for ApiClient, returned by useApi() in preview.
export const previewClient = {
  listSessions: () => delay(SESSIONS),
  sessionArchiveUrl: async () => {
    const blob = new Blob(["preview archive"], { type: "application/gzip" });
    return URL.createObjectURL(blob);
  },
  listTokens: () => delay(TOKENS),
  createToken: (name: string): Promise<CreatedToken> =>
    delay({ id: "tok_new", name, token: "stf_preview_3xAmpLe_t0k3n_d0n0tuse" }),
  revokeToken: () => delay(undefined as unknown as void),
  getUsage: () => delay(USAGE),
  listSkills: () => delay(SKILLS),
  getSkill: (key: SkillKey, version?: number) => {
    const head = previewHead(key);
    return delay(previewBundle(key, version ?? head, head));
  },
  getSkillHistory: (key: SkillKey) => {
    const head = previewHead(key);
    return delay(
      Array.from({ length: head }, (_, i) => previewBundle(key, head - i, head)),
    );
  },
  getSkillFile: (_key: SkillKey, path: string) =>
    delay(path.endsWith(".md") ? MD : "#!/bin/sh\necho smoke\n"),
  saveSkillFile: (key: SkillKey, _path: string, _content: string, parent: number) =>
    delay(previewBundle(key, parent + 1, parent + 1)),
  rollbackSkill: (key: SkillKey) => {
    const head = previewHead(key);
    return delay(previewBundle(key, head + 1, head + 1));
  },
  deleteSkill: () => delay(undefined as unknown as void),
  startCheckout: (): Promise<CheckoutResponse> =>
    delay({ url: "https://billing.stripe.com/preview" }),
  openBillingPortal: (): Promise<CheckoutResponse> =>
    delay({ url: "https://billing.stripe.com/preview" }),
} as unknown as ApiClient;
